import type { AnimalAlertSpecCreateOrUpdate } from '@/model/Alert.model';
import type { TreatmentType } from '@/model/Treatment.model';
import type { Entity, ID } from './Common.model';
import type { MetadataFieldWithValue } from './Metadata.model';
import type { TreatmentGroup } from './TreatmentGroup.model';
import { type PlannedAnimalDraft } from '@/model/Draft.model.ts';

export interface StudyForm extends Entity {
  name: string;
  type: 'internal' | 'external';
}

export interface BuildSummaryStepProps {
  handleSubmit: () => Promise<void>;
  formMeta: any;
}

export interface StudyFormMetadataItem {
  info: MetadataFieldWithValue;
  required: boolean;
}

interface StudyFormCages {
  dob: string;
  name: string;
  sex: string;
  subjects: number;
}

interface StudyFormMetadata {
  glossary_id: number;
  value: string | Array<string>;
}

interface StudyFormUsers {
  access: string;
  email: string;
  id: ID;
  name: string;
}

interface TreatmentValueUnit {
  value?: string;
  unit: string;
}

interface StudyFormTreatments {
  dose: TreatmentValueUnit;
  volume: TreatmentValueUnit;
  stock: TreatmentValueUnit;
  treatment_type?: TreatmentType;
  type: string;
  temp_id: ID;
  treatment_type_id: ID;
  treatment_name: string;
}

interface StudyFormAnimals {
  planned_animals?: Array<PlannedAnimalDraft>;
  registered_animals?: Array<PlannedAnimalDraft>;
}

export interface StudyFormPayload {
  alerts: Array<AnimalAlertSpecCreateOrUpdate>;
  average_calculation: 'arithmetic' | 'geometric';
  collections: Array<StudyFormCages>;
  draft_id?: number;
  error_deviation: 'sd' | 'sem';
  files: Array<unknown>;
  groups: Array<TreatmentGroup>;
  group_treatments: Array<{ group: string; treatments: Array<string> }>;
  animals?: StudyFormAnimals;
  animals_per_cage?: number;
  links: Array<unknown>;
  metadata: Array<StudyFormMetadata>;
  name: string;
  notes: string;
  owner?: ID;
  preset_id: ID;
  enable_consumption: boolean;
  project_id: ID;
  started_on: string;
  study_code_id?: ID;
  study_creation_template_id: ID;
  totalCages: string;
  treatments: Array<StudyFormTreatments>;
  type: 'internal' | 'external';
  users: Array<StudyFormUsers>;
}

export interface StudyFormSection extends Entity {
  active: boolean;
  default_section_number: number;
  metadata: Array<StudyFormMetadataItem>;
  slug: StudyCreateSectionSlug;
  name: string;
  type: string;
  metadataFields?: Array<MetadataFieldWithValue>;
}

export interface StudyFormMetaDetails extends Entity {
  name: string;
  sections: Array<StudyFormSection>;
  type: 'internal' | 'external';
}

type StudyCreateSectionSlug =
  | 'cages_animals'
  | 'groups'
  | 'presets'
  | 'alerts'
  | 'tasks'
  | 'attachments'
  | 'animals'
  | 'sampling_plan'
  | 'metadata';

export const studyCreateSectionSlug: Readonly<Record<StudyCreateSectionSlug, StudyCreateSectionSlug>> = Object.freeze({
  cages_animals: 'cages_animals',
  groups: 'groups',
  presets: 'presets',
  alerts: 'alerts',
  tasks: 'tasks',
  attachments: 'attachments',
  animals: 'animals',
  sampling_plan: 'sampling_plan',
  metadata: 'metadata',
} as const);
